import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShieldCheck, MapPin, Star, Flag, Store, ChevronRight, AlertTriangle, MessageCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { rp, CONDITIONS, fmtDate } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { ConditionBadge } from "@/components/ConditionBadge";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, lang } = useApp();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [reportReason, setReportReason] = useState("");

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => setP(data)).catch(() => setP(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (p === null) return <Layout><div className="py-24 text-center text-rl-muted">Memuat...</div></Layout>;
  if (p === false) return <Layout><div className="py-24 text-center text-rl-muted">Produk tidak ditemukan.</div></Layout>;

  const photos = [...(p.photos || []), ...(p.defect_photos || [])];
  const submitReport = async () => {
    if (!user) return navigate("/login");
    try { await api.post("/reports", { product_id: p.id, reason: reportReason, type: "counterfeit" });
      toast.success("Laporan terkirim. Tim kami akan meninjau."); setReportReason(""); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const startChat = async () => {
    if (!user) return navigate("/login", { state: { from: `/produk/${p.id}` } });
    if (!p.store?.id) return;
    navigate("/chat", { state: { start: { store_id: p.store.id, product_id: p.id, product_title: p.title } } });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6">
        <nav className="flex items-center gap-1 text-xs text-rl-muted mb-5">
          <Link to="/" className="hover:text-rl-primary">Home</Link><ChevronRight className="w-3 h-3" />
          <Link to={`/belanja?category=${p.category}`} className="hover:text-rl-primary">{p.category}</Link><ChevronRight className="w-3 h-3" />
          <span className="text-rl-ink2 truncate">{p.title}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden border border-rl-border bg-rl-bg2">
              {photos[activeImg] && <img src={photos[activeImg]} alt={p.title} className="w-full h-full object-cover" />}
            </div>
            {photos.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {photos.map((ph, i) => {
                  const isDefect = i >= (p.photos || []).length;
                  return (
                    <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                      className={`relative shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-rl-primary" : "border-rl-border"}`}>
                      <img src={ph} alt="" className="w-full h-full object-cover" />
                      {isDefect && <span className="absolute bottom-0 inset-x-0 bg-rl-primary/90 text-white text-[8px] font-bold text-center">MINUS</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {p.brand && <span className="text-xs uppercase tracking-widest font-semibold text-rl-muted">{p.brand}</span>}
            <h1 className="font-serif text-3xl font-bold text-rl-ink mt-1">{p.title}</h1>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <ConditionBadge condition={p.condition} />
              {p.negotiable && <span className="rounded-full bg-rl-secondary/10 text-rl-secondary border border-rl-secondary/20 px-3 py-0.5 text-xs font-semibold">Bisa Nego</span>}
            </div>
            <div className="mt-5 flex items-end gap-3">
              <span className="text-4xl font-bold text-rl-ink">{rp(p.price)}</span>
              {p.original_price && <span className="text-lg text-rl-muted line-through mb-1">{rp(p.original_price)}</span>}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button data-testid="detail-buy-now" onClick={() => { addToCart(p); navigate("/checkout"); }}
                className="rounded-full bg-rl-primary hover:bg-rl-primaryHover text-white font-bold py-3.5 transition-colors">Beli Sekarang</button>
              <button data-testid="detail-add-cart" onClick={() => { addToCart(p); toast.success("Ditambahkan ke keranjang"); }}
                className="rounded-full border border-rl-border hover:border-rl-primary text-rl-ink font-bold py-3.5 transition-colors">+ Keranjang</button>
            </div>

            {/* Seller card */}
            {p.store && (
              <Link to={`/toko/${p.store.slug}`} data-testid="seller-card"
                className="mt-6 flex items-center gap-3 rounded-2xl border border-rl-border bg-white p-4 hover:border-rl-primary transition-colors">
                <div className="w-12 h-12 rounded-full bg-rl-secondary/10 grid place-items-center"><Store className="w-6 h-6 text-rl-secondary" /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 font-bold text-rl-ink">{p.store.name}
                    {p.store.verified && <ShieldCheck className="w-4 h-4 text-rl-secondary" />}</div>
                  <div className="flex items-center gap-3 text-xs text-rl-muted mt-0.5">
                    <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-rl-gold text-rl-gold" />{p.store.rating || "Baru"}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.store.location}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-rl-muted" />
              </Link>
            )}
            {p.store && (
              <button data-testid="chat-seller-btn" onClick={startChat}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-rl-secondary text-rl-secondary hover:bg-rl-secondary hover:text-white font-semibold py-2.5 transition-colors">
                <MessageCircle className="w-4 h-4" />Chat Penjual
              </button>
            )}

            {/* Specs */}
            <div className="mt-6 rounded-2xl border border-rl-border bg-white p-5">
              <h3 className="font-bold text-rl-ink mb-3">Detail Barang</h3>
              <dl className="grid grid-cols-2 gap-y-2.5 text-sm">
                <Spec label="Kondisi" value={CONDITIONS[p.condition]?.[lang]} />
                {p.size && <Spec label="Ukuran" value={p.size} />}
                {p.color && <Spec label="Warna" value={p.color} />}
                {p.purchase_year && <Spec label="Tahun Beli" value={p.purchase_year} />}
                {p.brand && <Spec label="Merek" value={p.brand} />}
                <Spec label="Lokasi" value={p.location} />
              </dl>
              {p.authenticity && (
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-rl-secondary/5 border border-rl-secondary/15 p-3 text-sm text-rl-secondary">
                  <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" /><span><b>Keaslian:</b> {p.authenticity}</span>
                </div>
              )}
            </div>

            {/* Defect disclosure */}
            {p.defect_notes && (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-5">
                <h3 className="flex items-center gap-2 font-bold text-amber-800"><AlertTriangle className="w-4 h-4" />Catatan Kekurangan (Disclosed)</h3>
                <p className="mt-2 text-sm text-amber-900">{p.defect_notes}</p>
              </div>
            )}

            {/* Description */}
            <div className="mt-6">
              <h3 className="font-bold text-rl-ink mb-2">Deskripsi</h3>
              <p className="text-sm text-rl-ink2 leading-relaxed whitespace-pre-line">{p.description}</p>
              {p.shipping_info && <p className="mt-3 text-sm text-rl-muted"><b>Pengiriman:</b> {p.shipping_info}</p>}
            </div>

            {/* Report */}
            <Dialog>
              <DialogTrigger asChild>
                <button data-testid="report-btn" className="mt-6 inline-flex items-center gap-1.5 text-sm text-rl-muted hover:text-rl-primary">
                  <Flag className="w-4 h-4" />Laporkan produk (counterfeit / terlarang)</button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Laporkan Produk</DialogTitle></DialogHeader>
                <textarea data-testid="report-reason" value={reportReason} onChange={(e) => setReportReason(e.target.value)}
                  placeholder="Jelaskan alasan laporan..." className="w-full rounded-lg border border-rl-border p-3 text-sm min-h-[100px] outline-none focus:border-rl-primary" />
                <DialogFooter>
                  <button data-testid="report-submit" onClick={submitReport} disabled={!reportReason}
                    className="rounded-full bg-rl-primary hover:bg-rl-primaryHover text-white font-bold px-6 py-2.5 disabled:opacity-50">Kirim Laporan</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Reviews */}
        {p.reviews?.length > 0 && (
          <div className="mt-12">
            <h2 className="font-serif text-2xl font-semibold text-rl-ink mb-4">Ulasan Pembeli</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {p.reviews.map((r) => (
                <div key={r.id} className="rounded-xl border border-rl-border bg-white p-4">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-rl-ink">{r.buyer_name}</span>
                    <span className="flex items-center gap-0.5 text-rl-gold">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-rl-gold" />)}</span>
                    <span className="text-xs text-rl-muted ml-auto">{fmtDate(r.created_at)}</span>
                  </div>
                  <p className="mt-2 text-sm text-rl-ink2">{r.comment}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const Spec = ({ label, value }) => (
  <><dt className="text-rl-muted">{label}</dt><dd className="font-semibold text-rl-ink text-right">{value || "-"}</dd></>
);
